import type { ClickReleaseConfig, Corner } from '../config/types';
import { el } from '../util/dom';

/**
 * An unread badge overlaid on a host element. It lives in the shadow root and tracks the target's
 * viewport rect on scroll/resize/layout — it never inserts nodes into the host DOM, so it adds
 * zero layout shift to the host page.
 */
export class Indicator {
  #root: ShadowRoot;
  #target: Element;
  #side: Corner;
  #mode: 'dot' | 'count';
  #badge: HTMLElement;
  #visible = false;
  #ro: ResizeObserver | null = null;
  #raf = 0;

  constructor(root: ShadowRoot, target: Element, config: ClickReleaseConfig) {
    this.#root = root;
    this.#target = target;
    this.#side = config.indicator.side;
    this.#mode = config.indicator.mode;
    this.#badge = el('div', { class: `cr-badge cr-badge--${this.#mode}` });
    this.#root.append(this.#badge);
    this.#bind();
  }

  setCount(count: number): void {
    this.#visible = count > 0;
    if (this.#mode === 'count') this.#badge.textContent = count > 99 ? '99+' : String(count);
    this.#badge.classList.toggle('is-visible', this.#visible);
    this.position();
  }

  position(): void {
    if (!this.#visible) return;
    const rect = this.#target.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      this.#badge.classList.remove('is-visible');
      return;
    }
    this.#badge.classList.add('is-visible');

    const size = this.#badge.offsetWidth || 10;
    const half = size / 2;
    const top = this.#side.startsWith('top') ? rect.top - half : rect.bottom - size + half;
    const left = this.#side.endsWith('left') ? rect.left - half : rect.right - size + half;
    this.#badge.style.top = `${Math.round(top)}px`;
    this.#badge.style.left = `${Math.round(left)}px`;
  }

  destroy(): void {
    window.removeEventListener('scroll', this.#onReflow, true);
    window.removeEventListener('resize', this.#onReflow);
    this.#ro?.disconnect();
    this.#ro = null;
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#badge.remove();
  }

  #bind(): void {
    window.addEventListener('scroll', this.#onReflow, { passive: true, capture: true });
    window.addEventListener('resize', this.#onReflow, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      this.#ro = new ResizeObserver(() => this.position());
      this.#ro.observe(this.#target);
      this.#ro.observe(document.documentElement);
    }
  }

  #onReflow = (): void => {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.position();
    });
  };
}
